// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import * as React from "react";
import { FC, HTMLAttributes, useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-components";

const scrollUpClass = "scroll-up";
const scrollDownClass = "scroll-down";

export interface ScrollWrapperPresentationProps extends ExpandStyles<ScrollWrapperStyles> {
    maskSize?: number;
}

export interface ScrollWrapperProps extends ScrollWrapperPresentationProps, HTMLAttributes<HTMLDivElement> {}

export interface ScrollWrapperStyles {
    wrapper?: InjectableCss;
}

export const scrollWrapperStyles: ScrollWrapperStyles = {
    wrapper: {
        css: css`
            overflow-y: auto;
            transition:
                mask-position 125ms ease-out,
                mask-size 125ms ease-out;

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-thumb {
                background: rgba(150, 150, 150, 0.5);
                border-radius: 3px;
            }

            &::-webkit-scrollbar-track {
                background: transparent;
            }

            &.scroll-up {
                mask-position: center 0;
            }

            &.scroll-up.scroll-down {
                mask-position: center 0;
                mask-size: 100% 100%;
            }

            &.scroll-up,
            &.scroll-down {
                margin-right: calc(var(--scrollbar-width) * -1);
            }
        `,
    },
};

const ScrollWrapper: FC<ScrollWrapperProps> = ({
    maskSize = 30,
    children,
    className,
    extendedStyles,
    overridedStyles,
    ...otherProps
}) => {
    const styles = useExpandProps(scrollWrapperStyles, extendedStyles, overridedStyles);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);

    const wrapperStyles = useMemo(() => {
        return mergeProps<ScrollWrapperStyles["wrapper"]>(styles.wrapper, {
            css: css`
                mask-position: center -${maskSize}px;
                mask-size: 100% calc(100% + ${maskSize * 2}px);

                --scrollbar-width: ${scrollbarWidth}px;

                &.scroll-up {
                    mask-position: center 0;
                    mask-size: 100% calc(100% + ${maskSize}px);
                }

                &.scroll-down {
                    mask-position: center -${maskSize}px;
                    mask-size: 100% calc(100% + ${maskSize}px);
                }

                &.scroll-up,
                &.scroll-down {
                    mask-image: linear-gradient(
                            -90deg,
                            #000 var(--scrollbar-width),
                            transparent var(--scrollbar-width)
                        ),
                        linear-gradient(
                            0deg,
                            transparent,
                            #000 ${maskSize}px,
                            #000 calc(100% - ${maskSize}px),
                            transparent
                        );
                    margin-right: calc(var(--scrollbar-width) * -1);
                }
            `,
        });
    }, [styles.wrapper, scrollbarWidth, maskSize]);

    const updateScrollStatus = () => {
        const wrapper = wrapperRef.current;

        if (!wrapper) {
            return;
        }

        const isAtTop = wrapper.scrollTop === 0;
        const isAtBottom = Math.ceil(wrapper.scrollTop + wrapper.clientHeight) >= wrapper.scrollHeight;

        setCanScrollUp(!isAtTop);
        setCanScrollDown(!isAtBottom);
        setScrollbarWidth(wrapper.offsetWidth - wrapper.clientWidth);
    };

    useEffect(() => {
        const wrapper = wrapperRef.current;

        if (!wrapper) {
            return;
        }

        updateScrollStatus();

        wrapper.addEventListener("scroll", updateScrollStatus, { passive: true });
        window.addEventListener("resize", updateScrollStatus, { passive: true });

        return () => {
            wrapper.removeEventListener("scroll", updateScrollStatus);
            window.removeEventListener("resize", updateScrollStatus);
        };
    }, []);

    let _className = className || "";

    if (canScrollUp) {
        _className += _className ? ` ${scrollUpClass}` : scrollUpClass;
    }

    if (canScrollDown) {
        _className += _className ? ` ${scrollDownClass}` : scrollDownClass;
    }

    return (
        <StyledWrapper ref={wrapperRef} {...wrapperStyles} {...otherProps} className={_className}>
            {children}
        </StyledWrapper>
    );
};

export default ScrollWrapper;
